import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { View, Text, ScrollView } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { impact, ImpactStyle, notification, NotificationType } from '@/lib/utils/haptics'
import Animated, { FadeInDown } from 'react-native-reanimated'
import { AlertCircle, Pencil, ChevronRight } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { Pressable } from '@/components/ui/Pressable'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import {
  useListingWizardStore,
  WIZARD_STEPS,
  type WizardStep,
} from '@/lib/stores/listing-wizard.store'
import { useHostVehicles, useMakes, useModels } from '@/lib/hooks/use-host-vehicles'
import { vehicleImageService } from '@/lib/services/vehicle-image.service'
import { vehicleCardService } from '@/lib/services/vehicle-card.service'
import { vehicleCoverageReviewService } from '@/lib/services/coverage-review.service'
import { coverageEligibilityService } from '@/lib/services/coverage-eligibility.service'
import { vehicleService } from '@/lib/services/vehicle.service'
import { geocodingService } from '@/lib/services/geocoding.service'
import { useHostingEligibility } from '@/lib/hooks/use-hosting-eligibility'
import { useHostPublishReadiness } from '@/lib/hooks/use-host-publish-gate'
import { InsurerBadge } from '@/components/insurance/InsurerBadge'
import {
  vehicleDetailsSchema,
  vehicleFeaturesSchema,
  vehicleLocationSchema,
  vehiclePhotosSchema,
  vehiclePricingSchema,
} from '@/lib/validations/vehicle.schema'
import { vehicleDocumentSchema } from '@/lib/validations/vehicle-document.schema'
import { EARLY_ACCESS } from '@/lib/utils/constants'
import { useSemanticColors, SPACING, RADIUS, DURATION, TEXT_STYLES } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { toast } from '@/lib/toast'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { daysFromTodayISO } from '@/lib/utils/dates'
import { formatCurrency } from '@/lib/utils/format'
import { isCoordinateInElSalvador, normalizeElSalvadorVehicleLocation } from '@/lib/utils/geo'
import { marketTransmissionI18nKey } from '@/lib/utils/transmission'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import {
  pruneListingPhotoFiles,
  setListingUploadInFlight,
  stagedSourceKey,
} from '@/lib/utils/listing-photo-storage'
import { getShowcasePosition } from '@/lib/types/listing-photo.types'
import type { CreateVehicleInput } from '@/lib/types/vehicle.types'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { isExpectedNonFatalOperationalError } from '@/lib/errors/service-error'

/**
 * Un rechazo de diseño no es una avería.
 *
 * La póliza que excluye renta, uso comercial o transporte remunerado se
 * archiva en pending_review y se le explica al anfitrión en pantalla — es la
 * respuesta del verificador, no un defecto (TAGO-MOBILE-58). Reportarla a
 * Sentry convertía una decisión de producto en una alerta de producción y
 * escondía las fallas reales entre el ruido. Mismo criterio que el resto del
 * producto: `isExpectedNonFatalOperationalError` decide qué es esperado.
 */
function captureListingDefect(err: unknown, action: string, extra?: Record<string, unknown>): void {
  if (isExpectedNonFatalOperationalError(err)) return
  captureError(err, { screen: 'listing_review', action }, extra)
}

const SUBMIT_OPERATION_TIMEOUT_MS = {
  location: 6_000,
  saving: 30_000,
  photos: 120_000,
  documents: 90_000,
  coverage: 45_000,
  publishing: 12_000,
} as const

type SubmitPhase = 'idle' | 'saving' | 'photos' | 'documents' | 'coverage' | 'publishing'
type RemoteSubmitPhase = keyof typeof SUBMIT_OPERATION_TIMEOUT_MS

function submitPhaseI18nKey(phase: SubmitPhase): string | null {
  switch (phase) {
    case 'saving':
      return 'listing.submit_phase_saving'
    case 'photos':
      return 'listing.submit_phase_photos'
    case 'documents':
      return 'listing.submit_phase_documents'
    case 'coverage':
      return 'listing.submit_phase_coverage'
    case 'publishing':
      return 'listing.submit_phase_publishing'
    default:
      return null
  }
}

class ListingSubmitTimeoutError extends Error {
  phase: RemoteSubmitPhase

  constructor(phase: RemoteSubmitPhase, message: string) {
    super(message)
    this.name = 'ListingSubmitTimeoutError'
    this.phase = phase
  }
}

async function withSubmitTimeout<T>(
  operation: Promise<T>,
  phase: RemoteSubmitPhase,
  timeoutMessage: string,
): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new ListingSubmitTimeoutError(phase, timeoutMessage)),
      SUBMIT_OPERATION_TIMEOUT_MS[phase],
    )
  })

  try {
    return await Promise.race([operation, timeout])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

/**
 * The one way this screen says "nothing here yet".
 *
 * It used to have four: "Marca/modelo no seleccionado", "0 seleccionada(s)",
 * "No" and a bare em-dash — on the last screen a host sees before publishing.
 */
const REVIEW_EMPTY_VALUE = '—'

/** A count row's value: the number, or nothing at all when there is none. */
function countValue(count: number | undefined): string | undefined {
  return count ? String(count) : undefined
}

function ReviewRow({
  label,
  value,
  colors,
}: {
  label: string
  value: string | undefined | null
  colors: ReturnType<typeof useSemanticColors>
}) {
  // Never bail. `if (!value) return null` erased the ONLY row of the Ubicación
  // card whenever the address was blank, leaving a titled box with an "Editar"
  // link and 37pt of nothing under it while every sibling card carried rows.
  const isEmpty = !value

  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: SPACING.md,
        paddingVertical: SPACING.sm,
      }}
    >
      {/* The label sizes to its content instead of claiming half the row.
          With `flex: 1` on both sides the single word "Marca" reserved ~150pt
          of empty space while "Marca/modelo no seleccionado" wrapped to two
          ragged right-aligned lines next to it. */}
      <Text
        style={{
          fontSize: 14,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          flexShrink: 1,
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontSize: 14,
          fontFamily: FONTS.body,
          color: isEmpty ? colors.textMuted : colors.textPrimary,
          flex: 1,
          textAlign: 'right',
        }}
      >
        {isEmpty ? REVIEW_EMPTY_VALUE : value}
      </Text>
    </View>
  )
}

function SectionCard({
  title,
  children,
  colors,
  delay,
  testID,
  step,
  editTestID,
  summary,
}: {
  title: string
  children: React.ReactNode
  colors: ReturnType<typeof useSemanticColors>
  delay: number
  testID?: string
  /**
   * Wizard step this card summarises, BY NAME.
   *
   * It used to be a numeric index, and every caller hardcoded one. Inserting the
   * 'ownership' step shifted the list by one and silently broke six of the seven
   * Editar buttons — "Editar — Precio" opened the Location map — because nothing
   * relates a literal 4 to the card it sits on. The name is the identity, so the
   * index is derived here and cannot drift again.
   */
  step?: WizardStep
  editTestID?: string
  summary?: string
}) {
  const { t } = useTranslation()
  const router = useRouter()
  const goToStep = useListingWizardStore((s) => s.goToStep)
  const isEditEntry = useListingWizardStore((s) => s.entryMode === 'edit')

  const onEdit = () => {
    if (!step) return
    const index = WIZARD_STEPS.indexOf(step)
    if (index < 0) return
    // Mark the round trip so Back from that step returns HERE instead of
    // treating step 0 as "leave the wizard" and forcing a seven-step re-walk.
    useListingWizardStore.getState().setEditReturnTo('review')
    goToStep(index)
    const draft = useListingWizardStore.getState()
    const suffix =
      draft.entryMode === 'edit' && draft.vehicleId
        ? `?vehicleId=${encodeURIComponent(draft.vehicleId)}`
        : ''
    router.push(`/hosting/new-listing/${step}${suffix}` as never)
  }

  // Editing is a settings-style overview, not an eight-screen intake replay.
  // Keep the full review for new listings; existing hosts see their current
  // value and can tap anywhere on the row to change that section.
  if (isEditEntry && step) {
    return (
      <View testID={testID} style={{ marginTop: SPACING.sm }}>
        <Pressable
          onPress={onEdit}
          accessibilityRole="button"
          accessibilityLabel={`${t('common.edit')} — ${title}`}
          testID={editTestID}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.md,
            padding: SPACING.md,
            minHeight: 72,
            borderRadius: RADIUS.lg,
            backgroundColor: colors.surface,
          }}
        >
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}>
              {title}
            </Text>
            {summary ? (
              <Text
                numberOfLines={2}
                style={{
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                  marginTop: SPACING.xs,
                }}
              >
                {summary}
              </Text>
            ) : null}
          </View>
          <ChevronRight size={20} color={colors.textSecondary} />
        </Pressable>
      </View>
    )
  }

  return (
    <Animated.View
      collapsable={false}
      entering={FadeInDown.delay(delay).duration(DURATION.normal)}
      testID={testID}
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        marginTop: SPACING.md,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: SPACING.sm,
          marginBottom: SPACING.sm,
        }}
      >
        <Text
          style={{
            ...TEXT_STYLES.h4,
            fontFamily: FONTS.bodyBold,
            color: colors.textPrimary,
            flex: 1,
            minWidth: 0,
          }}
          numberOfLines={2}
        >
          {title}
        </Text>
        {step !== undefined ? (
          <Pressable
            onPress={onEdit}
            accessibilityRole="button"
            accessibilityLabel={`${t('common.edit')} — ${title}`}
            testID={editTestID}
            style={({ pressed }) => ({
              minWidth: 44,
              minHeight: 44,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACING.xs,
              paddingHorizontal: SPACING.sm,
              borderRadius: RADIUS.full,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <Pencil size={14} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            <Text style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textAccent }}>
              {t('common.edit')}
            </Text>
          </Pressable>
        ) : null}
      </View>
      {children}
    </Animated.View>
  )
}

function listingSubmitErrorMessage(
  err: unknown,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (err instanceof ListingSubmitTimeoutError) return err.message

  const message = err instanceof Error ? err.message.trim() : ''
  const userReadyMessages = new Set([
    t('errors.photo_upload_failed'),
    t('errors.document_upload_failed'),
    t('errors.policy_upload_failed'),
  ])

  if (message && userReadyMessages.has(message)) return message

  // The DB publish gates RAISE Spanish prose that IS the explanation the host
  // needs ("Agregá la tarjeta de circulación...", "Subí las 17 fotos..."). The
  // passthrough used accented patterns anchored with \b, but the triggers raise
  // their text unaccented and \b is ASCII-only, so "Agrega la tarjeta..." matched
  // nothing and every gate rejection collapsed into "Algo salió mal" — the host
  // was told the publish failed and never which requirement was missing.
  // Compare on a diacritic-stripped, lowercased copy so accented and unaccented
  // wording behave identically.
  const normalized = message.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  const PASSTHROUGH_PREFIXES = [
    'no se pudo',
    'agrega',
    'subi',
    'revisa',
    'debe',
    'solo',
    'por ahora',
    'la ',
    'el ',
    'ya existe',
    'falta',
    'tu cuenta',
  ]
  if (message && PASSTHROUGH_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return message
  }

  return errorMessage(err, t('errors.generic'))
}

export default function NewListingReviewScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { createVehicle, updateVehicle, isCreating, isUpdating } = useHostVehicles()
  const { currentOrganizationId, currentOrganization, eligibleOrganizations } =
    useHostingEligibility()
  const store = useListingWizardStore()
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle')
  // The catch below reported `phase: submitPhase` from a STALE closure, so every
  // failure logged "idle" and we could not tell which phase actually broke. The
  // ref always holds the live phase.
  const submitPhaseRef = useRef<SubmitPhase>('idle')
  const setPhase = useCallback((phase: SubmitPhase) => {
    submitPhaseRef.current = phase
    setSubmitPhase(phase)
  }, [])
  const [uploadProgress, setUploadProgress] = useState<{
    completed: number
    total: number
  } | null>(null)

  // Two different questions, and conflating them is what relabelled the wizard
  // after a failed publish:
  //  - `isEditMode`  — is there a row on the server to UPDATE? (submit logic)
  //  - `isEditEntry` — did the host OPEN this wizard to edit? (every label)
  // A failed publish leaves a draft row behind, which flips the first but must
  // never flip the second.
  const isEditMode = !!store.vehicleId
  const isEditEntry = store.entryMode === 'edit'
  const isBusy = isCreating || isUpdating || isSubmitting
  const submitPhaseKey = submitPhaseI18nKey(submitPhase)
  const { details, features, photos, location, pricing, documents, companyDetection } = store
  const documentsSkipped = useListingWizardStore((s) => s.documentsSkipped)
  const homeLocationPinnedByHost = useListingWizardStore((s) => s.homeLocationPinnedByHost)
  const policyDeferred = useListingWizardStore((s) => s.policyDeferred)
  // Same agency rule as documents step: only org listings choose own policy vs Qualitas.
  const isAgencyListing = Boolean(
    companyDetection?.organizationId ||
    companyDetection?.userChoice === 'create_org' ||
    companyDetection?.userChoice === 'join_org' ||
    (currentOrganizationId && companyDetection?.userChoice !== 'skip'),
  )
  const [qualitasEligible, setQualitasEligible] = useState(true)
  useEffect(() => {
    if (!details.category) return
    let active = true
    void coverageEligibilityService
      .isQualitasEligible(details.category, details.year ?? null)
      .then((eligible) => {
        if (active) setQualitasEligible(eligible)
      })
    return () => {
      active = false
    }
  }, [details.category, details.year])
  const hasTarjetaDocuments =
    !!documents.frontPhotoUri || !!documents.backPhotoUri || !!documents.documentNumber
  const hasOwnPolicy = Boolean(documents.insurancePolicyPhotoUri)
  // Qualitas path when the category is eligible and they did not upload (or
  // deferred) their own policy.
  const isQualitasPendingPath =
    qualitasEligible && hasTarjetaDocuments && (!hasOwnPolicy || policyDeferred)
  // Qualitas AML is a conditional capability, not a universal profile task.
  // Hosts using their own policy never need to complete it.
  const hostReadiness = useHostPublishReadiness({
    requiresPlatformCoverage: isQualitasPendingPath,
  })
  const shouldSubmitForReview = !isEditEntry && !documentsSkipped && isQualitasPendingPath
  // Say it BEFORE the tap, not after the upload. The publish gate was invisible
  // until the host had already shot 17 photos and waited through the upload, and
  // then it ejected them out of the wizard to /profile/verification — the single
  // most demoralising possible moment to learn a requirement exists. This is the
  // DEFAULT state of every first-time host (AML readiness is false with no
  // declaration), not an edge case. Same fail-open condition as the submit gate,
  // so this banner can never claim a block the gate would not actually apply.
  const willBlockAtPublish =
    hostReadiness.resolved &&
    !hostReadiness.passed &&
    !isEditEntry &&
    !documentsSkipped &&
    hasTarjetaDocuments
  // Sync guard — `isBusy` flips on the next render after the mutation kicks
  // off, so a double-tap before the first render can fire two `createVehicle`
  // calls. Mirrors the pattern in app/booking/checkout.tsx.
  const isSubmittingRef = useRef(false)
  const scrollRef = useRef<ScrollView>(null)

  /**
   * Show the error banner and put the submit button back.
   *
   * `recordValidationFailure` defaults true because seven of the eight callers
   * ARE pre-flight validation. The eighth is the catch at the end of the submit,
   * and it used to land here too — so every real publish failure ALSO emitted
   * listing.submit_validation_failed alongside listing.submit_failed. The funnel
   * is the instrument every other decision here is measured with, and it counted
   * a network timeout as an incomplete form.
   */
  const showSubmitError = useCallback(
    (message: string, recordValidationFailure = true) => {
      if (recordValidationFailure) {
        productTelemetryService.recordEvent('listing_step', 'listing.submit_validation_failed', {
          elementId: 'listing.review.submit',
          elementType: 'button',
          metadata: { messageLength: message.length },
        })
      }
      setError(message)
      isSubmittingRef.current = false
      setIsSubmitting(false)
      setPhase('idle')
      setUploadProgress(null)
      void notification(NotificationType.Error)
      requestAnimationFrame(() => {
        scrollRef.current?.scrollToEnd({ animated: true })
      })
    },
    [setPhase],
  )

  // Resolve make/model names from IDs
  const makesQuery = useMakes()
  const modelsQuery = useModels(details.makeId)
  // No bespoke "no seleccionado" string here: an unset make/model is the same
  // "nothing yet" every other unset row on this screen reports, and ReviewRow
  // now renders that one way.
  const makeName = makesQuery.data?.find((m) => m.id === details.makeId)?.name ?? ''
  const modelName = modelsQuery.data?.find((m) => m.id === details.modelId)?.name ?? ''
  const transmissionLabelKey = marketTransmissionI18nKey(
    'listing.transmissions',
    details.transmission,
  )

  const formatDollars = (cents: number | undefined): string => {
    if (cents === undefined) return '—'
    return formatCurrency(cents)
  }

  const formatMinRental = (hours: number | null | undefined): string => {
    if (hours === 48) return t('listing.min_rental_2_days')
    if (hours === 72) return t('listing.min_rental_3_days')
    return t('listing.min_rental_none')
  }

  const handleSubmit = async () => {
    if (isBusy || isSubmittingRef.current) return // prevent double-submit
    setError(null)
    isSubmittingRef.current = true
    setIsSubmitting(true)
    setPhase('saving')
    productTelemetryService.recordEvent('listing_step', 'listing.submit_started', {
      elementId: 'listing.review.submit',
      elementType: 'button',
      metadata: {
        editMode: isEditMode,
        photoCount: photos.photos?.length ?? 0,
        documentsSkipped,
        hasPolicyDocument: Boolean(documents.insurancePolicyPhotoUri),
        minRentalHours: pricing.minRentalHours ?? 0,
      },
    })
    impact(ImpactStyle.Light)

    // Validate all steps before submit
    const detailsResult = vehicleDetailsSchema.safeParse({
      ...details,
      seats: details.seats ?? 5,
      doors: details.doors ?? 4,
      plateCountry: details.plateCountry ?? 'SV',
    })
    if (!detailsResult.success) {
      showSubmitError(
        t('listing.step_details') +
          ': ' +
          detailsResult.error.issues.map((i) => t(i.message)).join(', '),
      )
      return
    }

    const featuresResult = vehicleFeaturesSchema.safeParse({
      title: features.title ?? '',
      description: features.description || undefined,
      features: features.features ?? [],
      rules: features.rules || undefined,
      isInstantBook: features.isInstantBook ?? false,
    })
    if (!featuresResult.success) {
      showSubmitError(
        t('listing.step_features') +
          ': ' +
          featuresResult.error.issues.map((i) => t(i.message)).join(', '),
      )
      return
    }

    const pricingResult = vehiclePricingSchema.safeParse({
      dailyRateCents: pricing.dailyRateCents ?? 0,
      insuranceModel: pricing.insuranceModel ?? EARLY_ACCESS.hostInsuranceModel,
      ...pricing,
    })
    if (!pricingResult.success) {
      showSubmitError(
        t('listing.step_pricing') +
          ': ' +
          pricingResult.error.issues.map((i) => t(i.message)).join(', '),
      )
      return
    }

    if (pricingResult.data.insuranceModel !== EARLY_ACCESS.hostInsuranceModel) {
      showSubmitError(t('listing.validation.insurance_agency_only'))
      return
    }

    const photosResult = vehiclePhotosSchema.safeParse({
      photos: photos.photos ?? [],
    })
    if (!photosResult.success) {
      showSubmitError(
        t('listing.step_photos') +
          ': ' +
          photosResult.error.issues.map((i) => t(i.message)).join(', '),
      )
      return
    }

    const locationAddress = location.homeAddress ?? ''
    let normalizedLocation = normalizeElSalvadorVehicleLocation({
      homeAddress: locationAddress,
      homeLat: location.homeLat,
      homeLng: location.homeLng,
    })

    // THE SECOND PLACE THAT USED TO MOVE THE CAR.
    //
    // Publishing re-geocoded the address and overwrote the coordinates all over again, so
    // fixing only the location step would have changed nothing: the pin the host dragged was
    // discarded here instead, one screen later and with no way to see it happen. Same rule,
    // same reason — the geocoder supplies coordinates we do not have, it does not correct the
    // ones the host chose.
    if (!homeLocationPinnedByHost) {
      try {
        const geocodedResults = await withSubmitTimeout(
          geocodingService.forward(locationAddress),
          'location',
          t('listing.submit_timeout_location'),
        )
        const geocoded = geocodedResults.find((result) =>
          isCoordinateInElSalvador(result.lat, result.lng),
        )
        if (geocoded) {
          normalizedLocation = {
            homeAddress: locationAddress.trim() || geocoded.address,
            homeLat: geocoded.lat,
            homeLng: geocoded.lng,
          }
        }
      } catch (err) {
        captureListingDefect(err, 'forward-geocode', {
          addressLength: locationAddress.trim().length,
        })
      }
    }

    const locationResult = vehicleLocationSchema.safeParse({
      homeAddress: normalizedLocation.homeAddress,
      homeLat: normalizedLocation.homeLat,
      homeLng: normalizedLocation.homeLng,
      countryId: location.countryId ?? 'SV',
      cityId: location.cityId,
      deliveryRadiusKm: location.deliveryRadiusKm,
    })
    if (!locationResult.success) {
      showSubmitError(
        t('listing.step_location') +
          ': ' +
          locationResult.error.issues.map((i) => t(i.message)).join(', '),
      )
      return
    }

    if (!documentsSkipped && (!isEditMode || documents.frontPhotoUri || documents.documentNumber)) {
      const documentsResult = vehicleDocumentSchema.safeParse({
        documentNumber: documents.documentNumber ?? '',
        expiryDate: documents.expiryDate ?? '',
        ownerName: documents.ownerName ?? '',
        frontPhotoUri: documents.frontPhotoUri ?? (isEditMode ? 'existing' : ''),
        frontPhotoCapturedAt: documents.frontPhotoCapturedAt,
        frontPhotoCaptureSource: documents.frontPhotoCaptureSource,
        backPhotoUri: documents.backPhotoUri ?? (isEditMode ? 'existing' : ''),
        backPhotoCapturedAt: documents.backPhotoCapturedAt,
        backPhotoCaptureSource: documents.backPhotoCaptureSource,
        tarjetaAiVerified: documents.tarjetaAiVerified === true,
        tarjetaBackAiVerified: documents.tarjetaBackAiVerified === true,
        insurancePolicyPhotoUri: documents.insurancePolicyPhotoUri ?? '',
        insurancePolicyPhotoCapturedAt: documents.insurancePolicyPhotoCapturedAt,
        insurancePolicyPhotoCaptureSource: documents.insurancePolicyPhotoCaptureSource,
        insurancePolicyNumber: documents.insurancePolicyNumber ?? '',
        insurancePolicyExpiryDate: documents.insurancePolicyExpiryDate ?? '',
        insuranceInsurerName: documents.insuranceInsurerName ?? '',
        insurancePolicyAiVerified: documents.insurancePolicyAiVerified === true,
      })
      if (!documentsResult.success) {
        showSubmitError(
          t('listing.step_documents') +
            ': ' +
            documentsResult.error.issues.map((i) => t(i.message)).join(', '),
        )
        return
      }
    }

    const companyDetection = useListingWizardStore.getState().companyDetection
    // Personal path (`skip`) must never inherit the host's org membership — that
    // would flip the vehicle into agency policy rules and Qualitas bypass.
    const listingOrganizationId =
      companyDetection?.userChoice === 'skip'
        ? undefined
        : (companyDetection?.organizationId ?? currentOrganizationId ?? undefined)
    const selectedOrganization = listingOrganizationId
      ? (eligibleOrganizations.find((org) => org.id === listingOrganizationId) ??
        (currentOrganization?.id === listingOrganizationId ? currentOrganization : null))
      : null
    const verifiedOrganizationFastTrack =
      !!listingOrganizationId && selectedOrganization?.isVerified === true
    // Prefer the resolved listing org over the screen-level heuristic so submit
    // status matches what we actually persist on the vehicle row.
    const submitAsAgency = Boolean(listingOrganizationId)

    const input: CreateVehicleInput = {
      ...detailsResult.data,
      ...featuresResult.data,
      ...pricingResult.data,
      homeAddress: locationResult.data.homeAddress,
      homeLat: locationResult.data.homeLat,
      homeLng: locationResult.data.homeLng,
      countryId: locationResult.data.countryId,
      cityId: locationResult.data.cityId,
      deliveryRadiusKm: locationResult.data.deliveryRadiusKm,
      insuranceModel: EARLY_ACCESS.hostInsuranceModel,
      organizationId: listingOrganizationId,
      companyOwnerDetected: companyDetection?.detected ?? false,
      detectedLegalName: companyDetection?.legalName ?? undefined,
      // The ownership step's answer, finally on its way to a column. Sent only when the host
      // actually gave one: `undefined` leaves whatever is stored alone, so a listing published
      // before the question existed is not stamped with an answer nobody gave.
      ownership: store.ownership ?? undefined,
    }

    let createdVehicleId: string | null = null
    // True when createVehicle handed back a REUSED row that was already published.
    let createdVehicleWasAlreadyLive = false
    // Seeded from the PERSISTED flag, not false. A retry skips documents it
    // already filed, so it never re-enters the catch that sets this — and asking
    // for status active with an unverified document is rejected by the DB gate,
    // forever, identically. The fact belongs to the server row, not the attempt.
    // PER DOCUMENT, not one shared flag. With a single scalar, a later document's
    // success cleared the pending mark an earlier one had just set — so a tarjeta
    // front that went in as pending was reported as verified because the policy
    // verified cleanly, submit then asked for 'active', and the DB completeness
    // trigger rejected it identically on every retry.
    //
    // Each is SEEDED from the persisted flag so a block that is skipped this
    // attempt (its URI is already in uploadedDocumentUris) inherits the pending
    // state instead of defaulting to verified.
    const seededNeedsReview = useListingWizardStore.getState().documentsNeedReview
    let frontNeedsReview = seededNeedsReview
    let backNeedsReview = seededNeedsReview
    let policyNeedsReview = seededNeedsReview
    let finalVehicleStatus: 'draft' | 'pending_review' | 'active' | 'paused' = 'draft'
    let statusUpdateStarted = false
    // The publish request timed out, so the row's real status is UNKNOWN. Never
    // let the success copy or the funnel report the status we asked for as the
    // status we got.
    let statusUpdateTimedOut = false
    let complianceBlockedAtPublish = false
    const hasPolicyDocumentForPublish = Boolean(documents.insurancePolicyPhotoUri)
    const hasRegistrationDocuments = Boolean(
      documents.frontPhotoUri || documents.backPhotoUri || documents.documentNumber,
    )
    const hasLocalDocumentUploads = Boolean(
      (documents.frontPhotoUri &&
        !documents.frontPhotoUri.startsWith('http') &&
        documents.frontPhotoUri !== 'existing') ||
      (documents.backPhotoUri &&
        !documents.backPhotoUri.startsWith('http') &&
        documents.backPhotoUri !== 'existing') ||
      (documents.insurancePolicyPhotoUri &&
        !documents.insurancePolicyPhotoUri.startsWith('http') &&
        documents.insurancePolicyPhotoUri !== 'existing'),
    )

    // Tell the pruner a publish is reading the capture files, so a discard or a
    // retake cannot delete them mid-flight.
    //
    // It used to be raised AFTER this createVehicle round trip, which left the
    // exact window it was written to protect uncovered: that call can run for
    // the full 'saving' timeout, and a host who taps the X during it — the most
    // likely moment to, since nothing has visibly happened yet — swept the
    // directory the upload loop was about to read. Raised here, every path out
    // of the try lowers it again in the `finally` below.
    setListingUploadInFlight(true)
    try {
      const vehicle = await withSubmitTimeout(
        isEditMode ? updateVehicle({ vehicleId: store.vehicleId!, input }) : createVehicle(input),
        'saving',
        t('listing.submit_timeout_saving'),
      )
      createdVehicleId = isEditMode ? null : vehicle.id
      // createVehicle does NOT always insert: on a plate collision it REUSES an
      // existing non-active row, and findReusablePlateDraft excludes only
      // 'active' — so a visible pending_review (or a deliberately paused) car can
      // come back here. mapCreateInput always inserts status 'draft', so anything
      // else means we are holding a row that already exists on the marketplace,
      // and the failure cleanup below must not force it to draft.
      createdVehicleWasAlreadyLive = !isEditMode && vehicle.status !== 'draft'
      if (!isEditMode) {
        store.setVehicleId(vehicle.id)
      }

      // Upload photos — only new local files, not existing server URLs, and not
      // the ones a previous attempt already got through. Nothing rewrites the
      // store's local URIs to the uploaded URLs, so before we tracked this a
      // second Publish tap after any later-phase failure re-sent all 17
      // inspection shots and the listing ended up with duplicate images.
      // We THROW on failure so the outer catch shows the error banner and
      // skips the celebration overlay. Better to block submit and let the
      // host retry than ship a photo-less listing live in Explore.
      const alreadyUploaded = new Set(useListingWizardStore.getState().uploadedPhotoUris)
      // Same shape for documents. verifyAndStoreDocument and
      // uploadPendingDocument each write a vehicle_documents row, and nothing
      // rewrites the store's local URI afterwards — so a retry after any
      // later-phase failure filed the tarjeta front, back and the policy scan a
      // second time. The photos already had this guard; the documents did not.
      const uploadedDocs = new Set(useListingWizardStore.getState().uploadedDocumentUris)
      const photoList = (photos.photos ?? []).filter(
        (p) => p.uri && !p.uri.startsWith('http') && !alreadyUploaded.has(p.uri),
      )
      // Positions stay stable across retries and follow the canonical showcase order:
      // front 3/4 beauty shots and interior cockpit get priority positions (0..9)
      // with front_right as position 0 (the cover). Technical inspection photos
      // (hood, engine, chassis rails, tools, odometer) take positions 10..16 so
      // the hero gallery is always pristine without any host friction.
      const positionByUri = new Map(
        (photos.photos ?? []).map(
          (p, index) => [p.uri, getShowcasePosition(p.requirementId, index)] as const,
        ),
      )
      const photoUploadAbort = new AbortController()
      if (photoList.length > 0) {
        setPhase('photos')
        // 17 sequential uploads with no counter is a two-minute dead spinner.
        setUploadProgress({ completed: 0, total: photoList.length })
        productTelemetryService.recordEvent('listing_step', 'listing.submit_photos_started', {
          elementId: 'listing.review.submit',
          elementType: 'button',
          metadata: { photoCount: photoList.length, skippedAlreadyUploaded: alreadyUploaded.size },
        })
        try {
          // Bytes that already travelled while the host was shooting become a server-side
          // copy instead of an upload. The map is keyed by the DIGEST of the uri (on web the
          // uri IS the photo), so the lookup hashes each one; a retake (new uri, new digest)
          // simply finds no record and uploads directly — stale bytes can never win.
          const stagedByUri = new Map(
            await Promise.all(
              photoList.map(async (p) => {
                const key = await stagedSourceKey(p.uri)
                return [p.uri, useListingWizardStore.getState().stagedPhotos[key]] as const
              }),
            ),
          )
          await withSubmitTimeout(
            vehicleImageService.uploadImages(
              vehicle.id,
              photoList.map((p) => ({
                uri: p.uri,
                position: positionByUri.get(p.uri) ?? 0,
                source: p.source,
                capturedAt: p.capturedAt,
                requirementId: p.requirementId,
                width: p.width,
                height: p.height,
                staged: stagedByUri.get(p.uri),
              })),
              // uploadImages walks the list in order and throws on the first
              // failure, so `completed` is exactly how many made it. Record them
              // as we go — a partial batch must not be re-sent either.
              (completed, total) => {
                setUploadProgress({ completed, total })
                const uploaded = photoList.slice(0, completed).map((p) => p.uri)
                if (uploaded.length > 0) {
                  useListingWizardStore.getState().markPhotosUploaded(uploaded)
                }
              },
              photoUploadAbort.signal,
            ),
            'photos',
            t('listing.submit_timeout_photos'),
          )
        } catch (uploadErr) {
          // A timeout ABANDONS the promise; it does not stop the loop. Trip the
          // signal so the in-flight batch stops after the photo it is on, or the
          // retry runs concurrently with it and both upload the same photos —
          // duplicate vehicle_images rows that the one-photo-per-slot schema then
          // rejects forever, locking the host out of ever editing that listing.
          photoUploadAbort.abort()
          if (uploadErr instanceof ListingSubmitTimeoutError) throw uploadErr
          // Surface the REAL reason (permission/session, size, network) instead
          // of a generic toast — a failing host photo upload was undiagnosable
          // before, which is exactly why a real host got a bare "sale error".
          // Report to Sentry and re-throw the specific, infra-free message.
          captureListingDefect(uploadErr, 'photo_upload')
          // Re-throw the ORIGINAL error, never a plain re-wrap. Wrapping it dropped
          // the ServiceError's userMessageKey, so an actionable RLS/session failure
          // ("cerrá sesión y volvé a entrar") degraded to the generic
          // "Algo salió mal", and the outer telemetry lost the real error identity.
          // listingSubmitErrorMessage resolves the user-facing text downstream.
          throw uploadErr
        }
      }

      setUploadProgress(null)

      // Verify + store required legal documents server-side. The Edge Function
      // reruns AI extraction and writes the approved vehicle_documents row with
      // a service-role key, so active publication does not trust client state.
      if (hasLocalDocumentUploads) {
        setPhase('documents')
        productTelemetryService.recordEvent('listing_step', 'listing.submit_documents_started', {
          elementId: 'listing.review.submit',
          elementType: 'button',
          metadata: {
            hasTarjetaFront: Boolean(documents.frontPhotoUri),
            hasTarjetaBack: Boolean(documents.backPhotoUri),
            hasPolicyDocument: Boolean(documents.insurancePolicyPhotoUri),
          },
        })
      }
      if (
        documents.frontPhotoUri &&
        !documents.frontPhotoUri.startsWith('http') &&
        documents.frontPhotoUri !== 'existing' &&
        !uploadedDocs.has(documents.frontPhotoUri)
      ) {
        try {
          await withSubmitTimeout(
            vehicleCardService.verifyAndStoreDocument({
              vehicleId: vehicle.id,
              photoUri: documents.frontPhotoUri,
              documentType: 'tarjeta_circulacion_front',
              captureSource: documents.frontPhotoCaptureSource,
              capturedAt: documents.frontPhotoCapturedAt,
              fileName: documents.frontPhotoFileName,
              mimeType: documents.frontPhotoMimeType,
              expected: {
                plateNumber: detailsResult.data.plateNumber,
                vin: detailsResult.data.vin || undefined,
                year: detailsResult.data.year,
                make: makeName,
                model: modelName,
                expiryDate: documents.expiryDate || undefined,
              },
            }),
            'documents',
            t('listing.submit_timeout_documents'),
          )
          // Verified cleanly: clear the latch. It used to be write-ONCE — set true
          // by a failed verification and never reset, so a host who fixed and
          // re-scanned their tarjeta still got pending_review forever, and an
          // agency car stayed marketplace-visible but unbookable until an admin
          // stepped in. A stale 'false' is safe: the DB completeness trigger
          // re-validates the documents and simply falls back to pending_review.
          frontNeedsReview = false
        } catch (docErr) {
          // A client-side TIMEOUT is not a verification failure: withSubmitTimeout
          // abandons the race but never aborts the request, so the edge function
          // may well have committed. Treating it as "failed" filed a SECOND,
          // pending row for the same document and latched needs-review. Re-throw
          // it like the photo path does and let the submit-level handler own it.
          if (docErr instanceof ListingSubmitTimeoutError) throw docErr
          frontNeedsReview = !verifiedOrganizationFastTrack
          if (__DEV__) console.warn('[Review] Document upload failed:', docErr)
          captureListingDefect(docErr, 'tarjeta_front_pending_review')
          await withSubmitTimeout(
            vehicleCardService.uploadPendingDocument({
              vehicleId: vehicle.id,
              photoUri: documents.frontPhotoUri,
              documentType: 'tarjeta_circulacion_front',
              captureSource: documents.frontPhotoCaptureSource,
              capturedAt: documents.frontPhotoCapturedAt,
              fileName: documents.frontPhotoFileName,
              mimeType: documents.frontPhotoMimeType,
              documentNumber: documents.documentNumber || detailsResult.data.plateNumber,
              expiryDate: documents.expiryDate || daysFromTodayISO(365),
              status: verifiedOrganizationFastTrack ? 'approved' : 'pending',
            }),
            'documents',
            t('listing.submit_timeout_documents'),
          )
        }
        // Filed either way — the catch path stores it as pending rather than
        // verified, but the row exists, so a retry must not file it again.
        useListingWizardStore.getState().markDocumentUploaded(documents.frontPhotoUri)
      }

      // SIBLING of the front block, not nested inside it. Nested, a back-only
      // rescan — the exact loop a host runs to fix a rejected tarjeta — was
      // skipped whenever the front was already filed (in edit mode frontPhotoUri
      // is the literal 'existing'), submit reported success, and activation then
      // failed server-side for a missing document the host was never told about.
      // The 'existing' guard matches its siblings so a re-publish does not re-file
      // a document the server already has.
      if (
        documents.backPhotoUri &&
        !documents.backPhotoUri.startsWith('http') &&
        documents.backPhotoUri !== 'existing' &&
        !uploadedDocs.has(documents.backPhotoUri)
      ) {
        try {
          await withSubmitTimeout(
            vehicleCardService.verifyAndStoreDocument({
              vehicleId: vehicle.id,
              photoUri: documents.backPhotoUri,
              documentType: 'tarjeta_circulacion_back',
              captureSource: documents.backPhotoCaptureSource,
              capturedAt: documents.backPhotoCapturedAt,
              fileName: documents.backPhotoFileName,
              mimeType: documents.backPhotoMimeType,
              expected: {
                plateNumber: detailsResult.data.plateNumber,
                vin: detailsResult.data.vin || undefined,
                year: detailsResult.data.year,
                make: makeName,
                model: modelName,
                expiryDate: documents.expiryDate || undefined,
              },
            }),
            'documents',
            t('listing.submit_timeout_documents'),
          )
          // Verified cleanly: clear the latch. It used to be write-ONCE — set true
          // by a failed verification and never reset, so a host who fixed and
          // re-scanned their tarjeta still got pending_review forever, and an
          // agency car stayed marketplace-visible but unbookable until an admin
          // stepped in. A stale 'false' is safe: the DB completeness trigger
          // re-validates the documents and simply falls back to pending_review.
          backNeedsReview = false
        } catch (docErr) {
          // A client-side TIMEOUT is not a verification failure: withSubmitTimeout
          // abandons the race but never aborts the request, so the edge function
          // may well have committed. Treating it as "failed" filed a SECOND,
          // pending row for the same document and latched needs-review. Re-throw
          // it like the photo path does and let the submit-level handler own it.
          if (docErr instanceof ListingSubmitTimeoutError) throw docErr
          backNeedsReview = !verifiedOrganizationFastTrack
          if (__DEV__) console.warn('[Review] Back document upload failed:', docErr)
          captureListingDefect(docErr, 'tarjeta_back_pending_review')
          await withSubmitTimeout(
            vehicleCardService.uploadPendingDocument({
              vehicleId: vehicle.id,
              photoUri: documents.backPhotoUri,
              documentType: 'tarjeta_circulacion_back',
              captureSource: documents.backPhotoCaptureSource,
              capturedAt: documents.backPhotoCapturedAt,
              fileName: documents.backPhotoFileName,
              mimeType: documents.backPhotoMimeType,
              documentNumber: documents.documentNumber || detailsResult.data.plateNumber,
              expiryDate: documents.expiryDate || daysFromTodayISO(365),
              status: verifiedOrganizationFastTrack ? 'approved' : 'pending',
            }),
            'documents',
            t('listing.submit_timeout_documents'),
          )
        }
        useListingWizardStore.getState().markDocumentUploaded(documents.backPhotoUri)
      }

      if (
        documents.insurancePolicyPhotoUri &&
        !documents.insurancePolicyPhotoUri.startsWith('http') &&
        documents.insurancePolicyPhotoUri !== 'existing' &&
        !uploadedDocs.has(documents.insurancePolicyPhotoUri)
      ) {
        try {
          await withSubmitTimeout(
            vehicleCardService.verifyAndStoreDocument({
              vehicleId: vehicle.id,
              photoUri: documents.insurancePolicyPhotoUri,
              documentType: 'insurance_policy',
              captureSource: documents.insurancePolicyPhotoCaptureSource,
              capturedAt: documents.insurancePolicyPhotoCapturedAt,
              fileName: documents.insurancePolicyFileName,
              mimeType: documents.insurancePolicyMimeType,
              expected: {
                plateNumber: detailsResult.data.plateNumber,
                vin: detailsResult.data.vin || undefined,
                year: detailsResult.data.year,
                make: makeName,
                model: modelName,
              },
            }),
            'documents',
            t('listing.submit_timeout_documents'),
          )
          // Same as the tarjeta blocks: a clean verification clears the latch, so
          // a fixed-and-rescanned policy is not stuck in pending_review forever.
          policyNeedsReview = false
        } catch (policyErr) {
          // A client-side TIMEOUT is not a verification failure: withSubmitTimeout
          // abandons the race but never aborts the request, so the edge function
          // may well have committed. Treating it as "failed" filed a SECOND,
          // pending row for the same document and latched needs-review. Re-throw
          // it like the photo path does and let the submit-level handler own it.
          if (policyErr instanceof ListingSubmitTimeoutError) throw policyErr
          policyNeedsReview = !verifiedOrganizationFastTrack
          if (__DEV__) console.warn('[Review] Insurance policy verification failed:', policyErr)
          captureListingDefect(policyErr, 'policy_pending_review')
          await withSubmitTimeout(
            vehicleCardService.uploadPendingDocument({
              vehicleId: vehicle.id,
              photoUri: documents.insurancePolicyPhotoUri,
              documentType: 'insurance_policy',
              captureSource: documents.insurancePolicyPhotoCaptureSource,
              capturedAt: documents.insurancePolicyPhotoCapturedAt,
              fileName: documents.insurancePolicyFileName,
              mimeType: documents.insurancePolicyMimeType,
              documentNumber:
                documents.insurancePolicyNumber || `MANUAL-${detailsResult.data.plateNumber}`,
              expiryDate: documents.insurancePolicyExpiryDate || daysFromTodayISO(365),
              status: verifiedOrganizationFastTrack ? 'approved' : 'pending',
            }),
            'documents',
            t('listing.submit_timeout_documents'),
          )
        }
        useListingWizardStore.getState().markDocumentUploaded(documents.insurancePolicyPhotoUri)
      }

      // Coverage review is best-effort and never blocks publish. Categories
      // outside the provider matrix use their own policy and never enter this
      // Qualitas delivery path.
      if (shouldSubmitForReview) {
        try {
          setPhase('coverage')
          productTelemetryService.recordEvent(
            'listing_step',
            'listing.submit_coverage_review_started',
            {
              elementId: 'listing.review.submit',
              elementType: 'button',
              metadata: { vehicleId: vehicle.id, editMode: isEditMode },
            },
          )
          const coverageReview = await withSubmitTimeout(
            vehicleCoverageReviewService.submitVehicleReview(vehicle.id),
            'coverage',
            t('listing.submit_timeout_coverage'),
          )
          productTelemetryService.recordEvent(
            'listing_step',
            'listing.submit_coverage_review_succeeded',
            {
              elementId: 'listing.review.submit',
              elementType: 'button',
              metadata: {
                vehicleId: vehicle.id,
                reviewId: coverageReview.review?.id ?? null,
                reviewStatus: coverageReview.review?.status ?? 'not_applicable',
                partnerConfigured: coverageReview.partnerConfigured ?? false,
                coverageApplicable: coverageReview.coverageApplicable ?? true,
              },
            },
          )
        } catch (coverageErr) {
          productTelemetryService.recordEvent(
            'listing_step',
            'listing.submit_coverage_review_failed',
            {
              elementId: 'listing.review.submit',
              elementType: 'button',
              metadata: { vehicleId: vehicle.id, editMode: isEditMode },
            },
          )
          captureListingDefect(coverageErr, 'submit_coverage_review_non_blocking', {
            vehicleId: vehicle.id,
            editMode: isEditMode,
          })
          const coverageMessage = errorMessage(
            coverageErr,
            t('listing.coverage_review_submit_failed'),
          )
          if (/más luz|more light|poca luz|underexposed|low_light|retake/i.test(coverageMessage)) {
            toast.error(coverageMessage)
          }
          if (__DEV__) console.warn('[Review] Coverage review non-blocking failure:', coverageErr)
        }
      }

      // Status rules:
      //  - No tarjeta → draft
      //  - Own AI-verified policy + no verification issues → active (bookable)
      //  - Otherwise (persona natural on the Qualitas / deferred-policy path) →
      //    pending_review: now VISIBLE in the marketplace (not a dead-end), and
      //    auto-promoted to active by the DB the moment coverage is approved.
      // This last write is intentionally bounded: production users were seeing
      // the DB commit succeed while the HTTP response never came back, leaving
      // the final review button spinning until they manually went back.
      try {
        setPhase('publishing')
        productTelemetryService.recordEvent('listing_step', 'listing.submit_publishing_started', {
          elementId: 'listing.review.submit',
          elementType: 'button',
          metadata: {
            editMode: isEditMode,
            isAgencyListing: submitAsAgency,
            hasOwnPolicy: hasPolicyDocumentForPublish,
          },
        })
        const statusWithoutPolicy =
          vehicle.status === 'active' || vehicle.status === 'pending_review'
            ? vehicle.status
            : 'draft'
        // Is this listing already on the marketplace? If so, re-saving it must
        // NEVER pull it offline — see the compliance guard below.
        const vehicleAlreadyLive =
          vehicle.status === 'active' || vehicle.status === 'pending_review'
        const noDocumentChanges =
          isEditMode &&
          !documents.frontPhotoUri &&
          !documents.backPhotoUri &&
          !documents.documentNumber &&
          !documents.insurancePolicyPhotoUri
        // Reaching `active` requires an own, AI-verified insurance policy — it is
        // NOT coupled to being an agency. An individual whose policy verifies
        // synchronously goes active; everyone else lands in pending_review, which
        // is now visible and auto-promotes to active on coverage approval. The DB
        // trigger enforce_active_vehicle_listing_completeness is the real gate, so
        // dropping the org coupling here cannot bypass any safety artifact.
        // Any single pending document keeps the whole listing in review, and the
        // OR is what we persist — so a retry inherits the pending state of every
        // document, including the ones it will skip because they are already filed.
        const anyDocumentNeedsReview = frontNeedsReview || backNeedsReview || policyNeedsReview
        useListingWizardStore.getState().setDocumentsNeedReview(anyDocumentNeedsReview)
        const canGoActive =
          hasPolicyDocumentForPublish && !anyDocumentNeedsReview && !policyDeferred
        // Publishing (active OR the now-visible pending_review) requires a
        // verified, AML-ready host. If not, keep the finished listing as a draft
        // and route them to verification after submit — no data loss, and no
        // step-1 wall that ejected them before they built anything.
        // Block ONLY when we AFFIRMATIVELY know the host is not compliant
        // (hostReadiness.resolved). While auth/KYC/AML are still loading we fail
        // OPEN so a spinner or cold cache never demotes a verified host. And we
        // NEVER demote a listing that is already live: re-saving an active or
        // pending_review car must not yank it off the marketplace or eject the
        // host — enforcing compliance on existing inventory is the DB trigger's
        // and admin tooling's job, not a silent client status write. New and
        // draft listings still get gated.
        complianceBlockedAtPublish =
          !vehicleAlreadyLive &&
          hostReadiness.resolved &&
          !hostReadiness.passed &&
          !noDocumentChanges &&
          !documentsSkipped &&
          hasRegistrationDocuments
        const desiredStatus = noDocumentChanges
          ? statusWithoutPolicy
          : documentsSkipped || !hasRegistrationDocuments || complianceBlockedAtPublish
            ? 'draft'
            : canGoActive
              ? 'active'
              : 'pending_review'
        // A RE-SAVE MUST NEVER RANK A LISTING DOWN. `canGoActive` requires
        // insurancePolicyPhotoUri, which only a live capture sets and nothing
        // hydrates in edit mode — so it is structurally false when a host reopens
        // a live car. Changing only the daily rate therefore wrote
        // pending_review over `active`, guests could no longer book it
        // (rentals_insert_guest gates on status='active'), and the host saw a
        // "¡Publicado!" toast while their car left the market. Enforcing
        // compliance on live inventory is the DB trigger's and admin tooling's
        // job, never a silent client downgrade.
        const STATUS_RANK = { draft: 0, paused: 0, pending_review: 1, active: 2 } as const
        const currentRank = STATUS_RANK[vehicle.status as keyof typeof STATUS_RANK] ?? 0
        const finalStatus =
          vehicleAlreadyLive && STATUS_RANK[desiredStatus] < currentRank
            ? (vehicle.status as typeof desiredStatus)
            : desiredStatus
        statusUpdateStarted = true
        finalVehicleStatus = finalStatus
        // Nothing to write: skip the call so a plain price edit does not re-run
        // the BEFORE-UPDATE completeness trigger against a listing that is
        // already live and already satisfied it.
        if (finalStatus === vehicle.status) {
          setPhase('idle')
        }
        finalVehicleStatus = await withSubmitTimeout(
          finalStatus === vehicle.status
            ? Promise.resolve(finalStatus)
            : vehicleService.setVehicleStatus(vehicle.id, finalStatus),
          'publishing',
          t('listing.submit_timeout_publishing'),
        )
      } catch (statusErr) {
        if (__DEV__) console.warn('[Review] Status update failed:', statusErr)
        if (!(statusErr instanceof ListingSubmitTimeoutError) || statusErr.phase !== 'publishing') {
          throw statusErr
        }
        captureListingDefect(statusErr, 'status_update_timeout', {
          vehicleId: vehicle.id,
          requestedStatus: finalVehicleStatus,
        })
        // If the status request timed out, do not demote the listing or keep
        // the user trapped on the spinner. The request may already have
        // committed server-side; the hosting dashboard refetch below is the
        // source of truth the user sees next.
        //
        // But we no longer know the row's status, so we must stop claiming it.
        // finalVehicleStatus is what we ASKED for, not what landed, and the
        // toast below read it straight: a host whose publish timed out was told
        // "¡Publicado! Ya aparece en la app" over a car still sitting in draft,
        // and left. Say what is true instead — the work is saved and we are
        // confirming — and keep the funnel honest about it.
        statusUpdateTimedOut = true
      }

      const finalSuccessMode =
        finalVehicleStatus === 'active'
          ? 'published'
          : finalVehicleStatus === 'pending_review'
            ? 'pending_review'
            : 'draft'
      // Past tense, because this fires AFTER the row was written as a draft.
      // Both this toast and the pre-tap banner further down used to read the
      // SAME key — `publish_needs_verification` was declared twice inside the
      // `listing` object in both locales, and JSON.parse keeps the last. So the
      // host who had just finished got the warning written for before they
      // started ("vas a necesitar… lo guardamos y te llevamos al final"), in
      // future tense, in green success chrome. The correct sentence existed the
      // whole time and had never once rendered.
      if (statusUpdateTimedOut) {
        toast.info(t('listing.publish_confirming'))
      } else {
        toast.success(
          complianceBlockedAtPublish
            ? t('listing.publish_saved_as_draft')
            : finalSuccessMode === 'published'
              ? t('hosting.vehicle_published')
              : finalSuccessMode === 'pending_review'
                ? t('hosting.vehicle_pending_review')
                : t('hosting.vehicle_draft_saved'),
        )
      }
      productTelemetryService.recordEvent('listing_step', 'listing.submit_succeeded', {
        elementId: 'listing.review.submit',
        elementType: 'button',
        metadata: {
          editMode: isEditMode,
          finalStatus: finalVehicleStatus,
          successMode: finalSuccessMode,
          // finalStatus above is the status we REQUESTED. When the publish
          // timed out we never learned what landed, so a run flagged here is
          // one whose finalStatus must not be read as fact.
          statusUnconfirmed: statusUpdateTimedOut,
          // Distinguish a host who hit the relocated KYC/AML wall (listing kept
          // as a draft, routed to verification) from a voluntary draft-save, so
          // the new friction point is measurable instead of invisible.
          complianceBlocked: complianceBlockedAtPublish,
        },
      })
      // Published: the captures are on the server now, so the local copies are
      // dead weight. reset() deliberately KEEPS files (it also runs on every
      // wizard entry and on involuntary sign-out), which left every successful
      // listing permanently orphaning ~17 full-res photos and ~3 document scans
      // in Paths.document — a directory the OS never reclaims and no in-app path
      // could free. This is the one moment deleting them is provably safe.
      // Release the in-flight guard FIRST or this sweep is dead code: the guard
      // in listing-photo-storage returns early while a publish is running, and it
      // is still set here. Every phase that reads capture files — photos AND the
      // document scans, which live in the same durable directory — has finished
      // by this point, and nothing below touches them, so releasing here cannot
      // pull a file out from under an upload. The finally's clear stays; the
      // setter is idempotent.
      setListingUploadInFlight(false)
      pruneListingPhotoFiles([])
      // The staging area served its purpose: every photo is now on its final key (copied or
      // directly uploaded), so the staged objects are orphans. Best-effort and awaited by
      // nobody — cleanup must never delay the success the host is looking at.
      void vehicleImageService.clearStagedListingPhotos()
      store.reset()
      void queryClient.invalidateQueries({ queryKey: ['host-vehicles'] })
      void queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      if (complianceBlockedAtPublish) {
        // Draft saved; route to exactly what's missing — the AML/Qualitas
        // compliance step when identity is already done, otherwise the KYC
        // identity hub. This is the relocated gate.
        router.replace(
          (hostReadiness.needsAml
            ? '/(tabs)/profile/verification/compliance'
            : '/(tabs)/profile/verification') as never,
        )
      } else {
        router.replace('/hosting')
      }
    } catch (err) {
      // Never force a REUSED, already-published row back to draft: that pulls a
      // live car off the marketplace as "cleanup" for a failure that had nothing
      // to do with its published state.
      if (
        !isEditMode &&
        createdVehicleId &&
        !statusUpdateStarted &&
        !createdVehicleWasAlreadyLive
      ) {
        try {
          await vehicleService.setVehicleStatus(createdVehicleId, 'draft')
        } catch (cleanupErr) {
          captureListingDefect(cleanupErr, 'preserve_failed_draft', {
            vehicleId: createdVehicleId,
          })
        }
      }
      const msg = listingSubmitErrorMessage(err, t)
      // "Guardamos el vehículo como borrador" has to survive EVERY failure, not
      // only the first. createdVehicleId is null on a retry (the row already
      // exists), so the second failure dropped the one sentence telling the host
      // their work is safe — precisely when they most need to hear it. Ask
      // whether THIS wizard session owns a server row instead.
      const wizardOwnsDraft = !isEditEntry && Boolean(useListingWizardStore.getState().vehicleId)
      const visibleMessage = wizardOwnsDraft
        ? `${msg}\n${t('listing.draft_preserved_after_publish_error')}`
        : msg
      // Diagnostics, not decoration. This event used to carry only the phase (from
      // a stale closure, so always "idle") and the LENGTH of the user-facing
      // string — which told us a publish failed but never why, and cost us days on
      // a real outage where every host was stranded. Record the actual error
      // identity so the next failure is diagnosable from the funnel alone. Goes to
      // our own analytics table; the user still sees only the i18n message.
      const errObj = err as { name?: string; code?: string; message?: string } | null
      productTelemetryService.recordEvent('listing_step', 'listing.submit_failed', {
        elementId: 'listing.review.submit',
        elementType: 'button',
        metadata: {
          editMode: isEditMode,
          phase: submitPhaseRef.current,
          draftPreserved: wizardOwnsDraft,
          errorName: typeof errObj?.name === 'string' ? errObj.name.slice(0, 60) : null,
          errorCode: typeof errObj?.code === 'string' ? errObj.code.slice(0, 60) : null,
          errorDetail: typeof errObj?.message === 'string' ? errObj.message.slice(0, 240) : null,
        },
      })
      // false: this is a real submit failure, already recorded above as
      // listing.submit_failed with its error identity and phase. Recording it a
      // second time as a validation failure is what made the two indistinguishable.
      showSubmitError(visibleMessage, false)
      toast.error(msg)
      captureListingDefect(err, isEditMode ? 'update' : 'create')
    } finally {
      setListingUploadInFlight(false)
      isSubmittingRef.current = false
      setIsSubmitting(false)
      setPhase('idle')
      setUploadProgress(null)
    }
  }
  // Arriving here ends any Editar round trip. Left set, a later genuine step-0
  // entry would skip the exit confirmation and try to pop a frame that is not
  // there.
  useEffect(() => {
    useListingWizardStore.getState().setEditReturnTo(null)
  }, [])

  const handleReviewScroll = useProductTelemetryScroll('listing.review.scroll')

  const pricingSection = (
    <SectionCard
      title={t('listing.step_pricing')}
      colors={colors}
      delay={150}
      testID="review.pricing-section"
      step="pricing"
      editTestID="review.edit-pricing"
      summary={`${formatDollars(pricing.dailyRateCents)}${t('vehicle_detail.per_day')}`}
    >
      <ReviewRow
        label={t('listing.daily_rate')}
        value={formatDollars(pricing.dailyRateCents)}
        colors={colors}
      />
      {pricing.securityDepositCents ? (
        <ReviewRow
          label={t('listing.security_deposit')}
          value={formatDollars(pricing.securityDepositCents)}
          colors={colors}
        />
      ) : null}
      <ReviewRow
        label={t('listing.min_rental_duration')}
        value={formatMinRental(pricing.minRentalHours)}
        colors={colors}
      />
      {pricing.weeklyDiscountPercent ? (
        <ReviewRow
          label={t('listing.weekly_discount')}
          value={`${pricing.weeklyDiscountPercent}%`}
          colors={colors}
        />
      ) : null}
      {pricing.dailyKmLimit ? (
        <ReviewRow
          label={t('listing.km_limit')}
          value={`${pricing.dailyKmLimit} km`}
          colors={colors}
        />
      ) : null}
    </SectionCard>
  )

  const photosSection = (
    <SectionCard
      title={t('listing.step_photos')}
      colors={colors}
      delay={125}
      testID="review.photos-section"
      step="photos"
      editTestID="review.edit-photos"
      summary={`${photos.photos?.length ?? 0} ${t('listing.step_photos').toLowerCase()}`}
    >
      <ReviewRow
        label={t('listing.step_photos')}
        value={countValue(photos.photos?.length)}
        colors={colors}
      />
    </SectionCard>
  )

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        scrollRef={scrollRef}
        onScroll={handleReviewScroll}
        scrollEventThrottle={64}
        contentContainerStyle={{
          padding: SPACING.lg,
          paddingBottom: SPACING['3xl'] + SPACING['2xl'] + SPACING.sm,
        }}
        style={{ opacity: isBusy ? 0.5 : 1, pointerEvents: isBusy ? 'none' : 'auto' }}
      >
        {/* Documents skipped warning */}
        {documentsSkipped && (
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              padding: SPACING.md,
              marginBottom: SPACING.md,
              backgroundColor: `${colors.warning}15`,
              borderRadius: RADIUS.md,
              borderLeftWidth: 3,
              borderLeftColor: colors.warning,
            }}
          >
            <AlertCircle size={18} color={colors.warning} />
            <Text
              style={{ fontSize: 13, fontFamily: FONTS.body, color: colors.textSecondary, flex: 1 }}
            >
              {t('listing.documents_pending_banner')}
            </Text>
          </Animated.View>
        )}

        {!isEditEntry &&
          !documentsSkipped &&
          (isQualitasPendingPath || (!qualitasEligible && !hasOwnPolicy)) && (
            <Animated.View
              collapsable={false}
              entering={enter(1)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                padding: SPACING.md,
                marginBottom: SPACING.md,
                backgroundColor: `${colors.warning}15`,
                borderRadius: RADIUS.md,
                borderLeftWidth: 3,
                borderLeftColor: colors.warning,
              }}
              testID="review.partial-documents-banner"
            >
              <AlertCircle size={18} color={colors.warning} />
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  flex: 1,
                }}
              >
                {!qualitasEligible
                  ? t('listing.non_platform_review_banner')
                  : isAgencyListing
                    ? t('listing.documents_partial_pending_banner')
                    : t('listing.individual_qualitas_review_banner')}
              </Text>
            </Animated.View>
          )}

        {isEditEntry ? (
          <>
            {pricingSection}
            {photosSection}
          </>
        ) : null}

        {/* Vehicle Details */}
        <SectionCard
          title={t('listing.step_details')}
          colors={colors}
          delay={50}
          testID="review.details-section"
          step="details"
          editTestID="review.edit-details"
          summary={[makeName, modelName, details.year].filter(Boolean).join(' · ')}
        >
          <ReviewRow label={t('listing.make')} value={makeName} colors={colors} />
          <ReviewRow label={t('listing.model')} value={modelName} colors={colors} />
          <ReviewRow label={t('listing.year')} value={details.year?.toString()} colors={colors} />
          <ReviewRow
            label={t('listing.category')}
            value={details.category ? t(`listing.categories.${details.category}`) : undefined}
            colors={colors}
          />
          <ReviewRow label={t('listing.color')} value={details.color} colors={colors} />
          <ReviewRow
            label={t('listing.transmission')}
            value={transmissionLabelKey ? t(transmissionLabelKey) : undefined}
            colors={colors}
          />
          <ReviewRow
            label={t('listing.fuel_type')}
            value={details.fuelType ? t(`listing.fuel_types.${details.fuelType}`) : undefined}
            colors={colors}
          />
          <ReviewRow label={t('listing.seats')} value={details.seats?.toString()} colors={colors} />
          <ReviewRow
            label={t('listing.plate_number')}
            value={details.plateNumber}
            colors={colors}
          />
        </SectionCard>

        {/* Ownership — the one answer on this screen that is a statement about someone else.
            It was asked in step 2 and then appeared nowhere: the host could not check it
            before publishing, and for a third-party car they were naming a real person as
            having authorised them, sight unseen. */}
        <SectionCard
          title={t('listing.step_ownership')}
          colors={colors}
          delay={65}
          testID="review.ownership-section"
          step="ownership"
          editTestID="review.edit-ownership"
          summary={
            store.ownership
              ? t(`listing.ownership_${store.ownership.type}_title`)
              : t('listing.ownership_not_declared')
          }
        >
          <ReviewRow
            label={t('listing.ownership_title')}
            value={
              store.ownership
                ? t(`listing.ownership_${store.ownership.type}_title`)
                : // Only reachable on a listing older than the column: nobody was asked.
                  // Says so, instead of showing a blank that reads like a bug.
                  t('listing.ownership_not_declared')
            }
            colors={colors}
          />
          {store.ownership?.type === 'third_party' ? (
            <ReviewRow
              label={t('listing.ownership_authorizer_label')}
              value={store.ownership.authorizedByName}
              colors={colors}
            />
          ) : null}
        </SectionCard>

        {/* Features */}
        <SectionCard
          title={t('listing.step_features')}
          colors={colors}
          delay={100}
          testID="review.features-section"
          step="features"
          editTestID="review.edit-features"
          summary={features.title}
        >
          <ReviewRow label={t('listing.title')} value={features.title} colors={colors} />
          {/* Just the count. The old value ran it through
              `listing.features_selected`, whose Spanish form is
              "{{count}} seleccionada(s)" — so the final confirmation screen
              showed "0 seleccionada(s)", parenthesised plural suffix and all.
              A bare number next to its own label says the same thing, and an
              unset count falls through to the screen's one empty convention. */}
          <ReviewRow
            label={t('listing.features_label')}
            value={countValue(features.features?.length)}
            colors={colors}
          />
          <ReviewRow
            label={t('listing.instant_book')}
            value={features.isInstantBook ? t('common.yes') : t('common.no')}
            colors={colors}
          />
        </SectionCard>

        {/* Photos */}
        {!isEditEntry ? photosSection : null}

        {/* Location */}
        <SectionCard
          title={t('listing.step_location')}
          colors={colors}
          delay={135}
          testID="review.location-section"
          step="location"
          editTestID="review.edit-location"
          summary={location.homeAddress}
        >
          <ReviewRow label={t('listing.address')} value={location.homeAddress} colors={colors} />
          {location.deliveryRadiusKm ? (
            <ReviewRow
              label={t('listing.delivery_radius')}
              value={`${location.deliveryRadiusKm} km`}
              colors={colors}
            />
          ) : null}
        </SectionCard>

        {/* Pricing */}
        {!isEditEntry ? pricingSection : null}

        {/* Verification needed to publish — surfaced HERE, before the host taps
            Publicar, so the requirement is never a surprise sprung after the
            upload. Non-blocking: the listing is still saved as a draft either
            way, and this only tells the truth earlier. */}
        {willBlockAtPublish ? (
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            testID="review.publish-needs-verification"
            style={{
              marginTop: SPACING.md,
              padding: SPACING.md,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.warning,
              backgroundColor: colors.surface,
              flexDirection: 'row',
              gap: SPACING.sm,
            }}
          >
            <AlertCircle size={18} color={colors.warning} strokeWidth={2} />
            <Text
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                lineHeight: 19,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('listing.publish_needs_verification')}
            </Text>
          </Animated.View>
        ) : null}

        {/* Insurance */}
        <SectionCard
          title={t('listing.step_insurance')}
          colors={colors}
          delay={158}
          testID="review.insurance-section"
          /* 'insurance' is a redirect route, not a wizard step, so there is no
             step of its own to jump to. Of the two rows here only the policy
             document is something the host can change — insuranceModel is a
             constant (EARLY_ACCESS.hostInsuranceModel), not a choice — so Editar
             goes where that edit actually happens. */
          step="documents"
          editTestID="review.insurance-edit"
          summary={t('listing.policy_document')}
        >
          <ReviewRow
            label={t('listing.insurance_model')}
            value={
              qualitasEligible
                ? t('listing.insurance_agency_summary')
                : t('listing.policy_non_platform')
            }
            colors={colors}
          />
          <ReviewRow
            label={t('listing.policy_document')}
            value={
              documents.insurancePolicyAiVerified ? t('listing.policy_ai_approved') : undefined
            }
            colors={colors}
          />
          {qualitasEligible ? (
            <View style={{ marginTop: SPACING.sm }}>
              <InsurerBadge variant="horizontal" testID="review.coverage-provider-badge" />
            </View>
          ) : (
            <Text
              style={{
                marginTop: SPACING.sm,
                fontSize: 13,
                lineHeight: 19,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('listing.policy_non_platform_notice')}
            </Text>
          )}
        </SectionCard>

        {/* Documents */}
        {documents.documentNumber || documents.ownerName || documents.frontPhotoUri ? (
          <SectionCard
            title={t('listing.step_documents')}
            colors={colors}
            delay={175}
            testID="review.documents-section"
            step="documents"
            editTestID="review.edit-documents"
            summary={[documents.documentNumber, documents.expiryDate].filter(Boolean).join(' · ')}
          >
            <ReviewRow
              label={t('listing.tarjeta_circulacion')}
              value={
                documents.tarjetaAiVerified && documents.tarjetaBackAiVerified
                  ? t('listing.tarjeta_ai_approved')
                  : undefined
              }
              colors={colors}
            />
            <ReviewRow
              label={t('listing.tarjeta_expiry')}
              value={documents.expiryDate}
              colors={colors}
            />
            <ReviewRow
              label={t('listing.tarjeta_owner')}
              value={documents.ownerName}
              colors={colors}
            />
            <ReviewRow
              label={t('listing.policy_number')}
              value={documents.insurancePolicyNumber}
              colors={colors}
            />
            <ReviewRow
              label={t('listing.policy_expiry')}
              value={documents.insurancePolicyExpiryDate}
              colors={colors}
            />
          </SectionCard>
        ) : null}

        {/* Error */}
        {error ? (
          <Animated.View
            collapsable={false}
            entering={enter()}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
            testID="review.error-banner"
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: SPACING.sm,
              padding: SPACING.md,
              marginTop: SPACING.md,
              backgroundColor: `${COLORS.coral.DEFAULT}15`,
              borderRadius: RADIUS.md,
              borderWidth: 1,
              borderColor: `${COLORS.coral.DEFAULT}55`,
            }}
          >
            <AlertCircle size={20} color={COLORS.coral.DEFAULT} />
            <Text
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: 20,
                fontFamily: FONTS.body,
                color: colors.textAccent,
              }}
            >
              {error}
            </Text>
          </Animated.View>
        ) : null}
      </KeyboardAwareView>

      {/* Bottom CTA — same primitive as the other six steps. The action bar is
          the only thing still legible while the ScrollView above is dimmed to
          0.5, so the counter has to live here.

          NOT inside the Button: `loading` fades the Button's own label to
          opacity 0 and swaps in the spinner (components/ui/Button.tsx), so a
          phase or a count passed as `title` is never actually on screen. */}
      <FormActionBar style={{ backgroundColor: colors.background }}>
        {isBusy ? (
          <View
            accessibilityLiveRegion="polite"
            testID="review.submit-progress"
            style={{ gap: SPACING.xs, marginBottom: SPACING.sm }}
          >
            <Text
              numberOfLines={1}
              style={{
                fontSize: 13,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                textAlign: 'center',
              }}
            >
              {submitPhase === 'photos' && uploadProgress
                ? t('listing.submit_phase_photos_progress', {
                    completed: uploadProgress.completed,
                    total: uploadProgress.total,
                  })
                : submitPhaseKey
                  ? t(submitPhaseKey)
                  : t('common.loading')}
            </Text>
            {submitPhase === 'photos' && uploadProgress && uploadProgress.total > 0 ? (
              <ProgressBar
                value={uploadProgress.completed / uploadProgress.total}
                height={4}
                fillColor={COLORS.coral.DEFAULT}
                trackColor={colors.surfaceSubtle}
                borderRadius={RADIUS.full}
              />
            ) : null}
          </View>
        ) : null}
        <Button
          onPress={handleSubmit}
          variant="primary"
          size="lg"
          title={
            isBusy
              ? t('common.loading')
              : isEditEntry
                ? t('common.save')
                : documentsSkipped
                  ? t('listing.save_draft')
                  : shouldSubmitForReview
                    ? t('listing.submit_for_review')
                    : t('listing.publish')
          }
          loading={isBusy}
          disabled={isBusy || (isEditEntry && !store.dirtySinceLoad)}
          testID="review.submit-button"
        />
      </FormActionBar>
    </ScreenContainer>
  )
}
